import { apis } from "common/apis";
import { routes } from "common/routes";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import {
  downloadGigWorkerOnboarding,
  downloadEarningPayouts,
  getEarningPayouts,
  getGigWorkerPayouts,
  markSuccessGigWorkerPayouts,
  processGigWorkerPayouts,
  removeProcessingAlterEarnings,
  removeProcessingEarningPayouts,
  removeProcessingGigPayouts,
  saveProcessingAlterEarnings,
  cancelProcessingAlterEarnings,
  saveProcessingEarningPayouts,
  cancelProcessingEarningPayouts,
  saveProcessingGigPayouts,
  cancelProcessingGigPayouts,
  setProcessingAlterEarnings,
  setProcessingEarningPayouts,
  setProcessingGigPayouts,
} from "store/partner-services/partnerServicesSlice";
import {
  downloadWhEarningPayouts,
  downloadWhPartnerOnboarding,
  getWhEarningPayouts,
  getWhPartnerPayouts,
  markSuccessWhPartnerPayouts,
  processWhPartnerPayouts,
  removeWhProcessingAlterEarnings,
  removeWhProcessingEarningPayouts,
  removeWhProcessingGigPayouts,
  saveWhProcessingAlterEarnings,
  cancelWhProcessingAlterEarnings,
  saveWhProcessingEarningPayouts,
  cancelWhProcessingEarningPayouts,
  saveWhProcessingGigPayouts,
  cancelWhProcessingGigPayouts,
  setWhProcessingAlterEarnings,
  setWhProcessingEarningPayouts,
  setWhProcessingGigPayouts,
} from "store/wh-partner-services/whPartnerServicesSlice";

export type PartnerVariant = "ppd" | "wh";

type PartnerRoutes = {
  SAVE_GIG_PAYOUTS: string;
  UPLOAD_GIG_PAYOUTS: string;
  EARNING_PAYOUTS: string;
  SAVE_EARNING_PAYOUTS: string;
  UPLOAD_EARNING_PAYOUTS: string;
  SAVE_ALTER_EARNINGS: string;
  UPLOAD_ALTER_EARNINGS: string;
  PROCESS_EARNINGS: string;
};

/**
 * Returns the correct Redux state slice + stable thunk/action creator references
 * for the given variant ("ppd" or "wh"). Thunk and action creators are
 * module-level constants so their references are stable across renders —
 * safe to use as useCallback / useEffect dependencies without causing infinite loops.
 */
export const usePartnerServicesConfig = (variant: PartnerVariant = "ppd") => {
  const dispatch = useAppDispatch();
  const ppd = useAppSelector((s) => s.partnerServices);
  const wh = useAppSelector((s) => s.whPartnerServices);

  // Pick the right state slice. This changes when Redux state changes (expected).
  const svc = variant === "wh" ? wh : ppd;

  // Routes — module-level objects, same reference every render.
  const partnerRoutes: PartnerRoutes =
    variant === "wh" ? routes.WH_PARTNER_SERVICES : routes.PARTNER_SERVICES;

  // API URLs — strings derived from variant (stable for the component lifetime).
  const uploadOnboardingUrl =
    variant === "wh" ? apis.whUploadOnboardingDetails : apis.uploadOnboardingDetails;
  const saveOnboardingUrl =
    variant === "wh" ? apis.whSaveOnboardingDetails : apis.saveOnboardingDetails;
  const uploadGigPayoutsUrl =
    variant === "wh" ? apis.whUploadPartnerPayouts : apis.uploadGigPayouts;
  const uploadAlterEarningsUrl =
    variant === "wh" ? apis.whUploadEarningsAlteration : apis.uploadEarningsAlteration;

  // ── Stable thunk / action-creator references ──────────────────────────────
  // These are module-level constants from the slice files. The ternary always
  // resolves to the same reference (variant never changes for a mounted component),
  // so these are safe to use as useCallback / useEffect deps.

  const downloadOnboardingThunk =
    variant === "wh" ? downloadWhPartnerOnboarding : downloadGigWorkerOnboarding;

  const earningPayoutsThunk =
    variant === "wh" ? getWhEarningPayouts : getEarningPayouts;
  const downloadEarningPayoutsThunk =
    variant === "wh" ? downloadWhEarningPayouts : downloadEarningPayouts;

  const saveGigPayoutsThunk =
    variant === "wh" ? saveWhProcessingGigPayouts : saveProcessingGigPayouts;
  const cancelGigPayoutsThunk =
    variant === "wh" ? cancelWhProcessingGigPayouts : cancelProcessingGigPayouts;

  const saveEarningPayoutsThunk =
    variant === "wh" ? saveWhProcessingEarningPayouts : saveProcessingEarningPayouts;
  const cancelEarningPayoutsThunk =
    variant === "wh" ? cancelWhProcessingEarningPayouts : cancelProcessingEarningPayouts;

  const saveAlterEarningsThunk =
    variant === "wh" ? saveWhProcessingAlterEarnings : saveProcessingAlterEarnings;
  const cancelAlterEarningsThunk =
    variant === "wh" ? cancelWhProcessingAlterEarnings : cancelProcessingAlterEarnings;

  const getPayoutsThunk =
    variant === "wh" ? getWhPartnerPayouts : getGigWorkerPayouts;
  const processPayoutsThunk =
    variant === "wh" ? processWhPartnerPayouts : processGigWorkerPayouts;
  const markSuccessPayoutsThunk =
    variant === "wh" ? markSuccessWhPartnerPayouts : markSuccessGigWorkerPayouts;

  // Action creators (reducer actions — same stability guarantee as thunks).
  const setGigPayoutsAction =
    variant === "wh" ? setWhProcessingGigPayouts : setProcessingGigPayouts;
  const removeGigPayoutsAction =
    variant === "wh" ? removeWhProcessingGigPayouts : removeProcessingGigPayouts;

  const setEarningPayoutsAction =
    variant === "wh" ? setWhProcessingEarningPayouts : setProcessingEarningPayouts;
  const removeEarningPayoutsAction =
    variant === "wh" ? removeWhProcessingEarningPayouts : removeProcessingEarningPayouts;

  const setAlterEarningsAction =
    variant === "wh" ? setWhProcessingAlterEarnings : setProcessingAlterEarnings;
  const removeAlterEarningsAction =
    variant === "wh" ? removeWhProcessingAlterEarnings : removeProcessingAlterEarnings;

  return {
    dispatch,

    // ── State (from the correct slice) ────────────────────────────────────
    processingGigPayouts: svc.processingGigPayouts,
    processingEarningPayouts: svc.processingEarningPayouts,
    processingAlterEarnings: svc.processingAlterEarnings,
    earningPayouts: svc.earningPayouts,
    payouts: svc.payouts,
    status: svc.status,
    error: svc.error,
    payoutsStatus: svc.payoutsStatus,
    payoutsError: svc.payoutsError,
    earningPayoutsDownloadStatus: svc.earningPayoutsDownloadStatus,
    onboardingDownloadStatus: svc.onboardingDownloadStatus,

    // ── API URLs ──────────────────────────────────────────────────────────
    uploadOnboardingUrl,
    saveOnboardingUrl,
    uploadGigPayoutsUrl,
    uploadAlterEarningsUrl,

    // ── Routes ───────────────────────────────────────────────────────────
    partnerRoutes,

    // ── Thunk creators (use with dispatch) ───────────────────────────────
    downloadOnboardingThunk,
    earningPayoutsThunk,
    downloadEarningPayoutsThunk,
    saveGigPayoutsThunk,
    cancelGigPayoutsThunk,
    saveEarningPayoutsThunk,
    cancelEarningPayoutsThunk,
    saveAlterEarningsThunk,
    cancelAlterEarningsThunk,
    getPayoutsThunk,
    processPayoutsThunk,
    markSuccessPayoutsThunk,

    // ── Action creators (use with dispatch) ──────────────────────────────
    setGigPayoutsAction,
    removeGigPayoutsAction,
    setEarningPayoutsAction,
    removeEarningPayoutsAction,
    setAlterEarningsAction,
    removeAlterEarningsAction,
  };
};
